import React, { useEffect, useState } from 'react';
import { supabase, isSupabaseEnabled } from '../lib/supabaseClient';
import { calcDivergencePoints } from '../lib/divergence';

// Post-enrolment Ghost claim surface — implements
// docs/audit/2026-05-14-arena-enrolment-brief.md §4 GhostClaimModal.
//
// The parent (the cockpit) mounts this modal after EnrolmentModal
// succeeds, passing the citizen_XXX Ghost id from localStorage and
// the new enrolled profile id. The modal queries pledges to surface
// the inheritance, then offers Claim / Skip:
//
//   Claim → calls public.claim_ghost_identity(p_ghost_profile_id).
//           On success the parent reloads the cockpit so the
//           effective_owner-aware DP / history aggregation picks
//           the claimed Ghost up.
//   Skip  → no claim recorded. The Ghost row stays in public.profiles
//           under its citizen_XXX id; new pledges land under the
//           enrolled identity from here on.
//
// Ledger Shield: claim_ghost_identity never mutates pledges,
// settlements, or the Ghost profile row — it only INSERTs into
// public.identity_claims. The Ghost id remains the on-ledger author
// of historical pledges forever; the indirection table is what
// surfaces them under the enrolled identity going forward.

interface Props {
  ghostProfileId:     string;
  enrolledProfileId:  string;
  onDone:             (claimed: boolean) => void;
}

interface PledgeRow {
  payout_units:    number | string | null;
  effective_price: number | string | null;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'empty' }                                  // nothing to claim — parent should skip
  | { kind: 'offer';     pledgeCount: number; dp: number }
  | { kind: 'claiming';  pledgeCount: number; dp: number }
  | { kind: 'verifying'; pledgeCount: number; dp: number }
  | { kind: 'claimed';   pledgeCount: number; dp: number; previously: boolean; verifiedCount: number | null }
  | { kind: 'error';     pledgeCount: number; dp: number; message: string };

export default function GhostClaimModal({
  ghostProfileId, enrolledProfileId, onDone,
}: Props): React.JSX.Element | null {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  // ── Fetch inheritance counts on mount ────────────────────────────
  useEffect(() => {
    console.log('[GhostClaimModal] mounted with', { ghostProfileId, enrolledProfileId });
    let cancelled = false;
    void (async () => {
      if (!isSupabaseEnabled() || !supabase) {
        console.warn('[GhostClaimModal] Supabase not enabled — self-dismissing');
        if (!cancelled) setState({ kind: 'empty' });
        return;
      }
      try {
        const { data, error } = await supabase
          .from('pledges')
          .select('payout_units, effective_price, market_id, outcome_name')
          .eq('user_id', ghostProfileId);
        if (cancelled) return;
        if (error) {
          console.error('[GhostClaimModal] pledge query failed', error);
          setState({ kind: 'empty' });
          return;
        }
        const rows  = (data ?? []) as PledgeRow[];
        const count = rows.length;
        let dp = 0;
        for (const r of rows) {
          dp += calcDivergencePoints(
            Number(r.payout_units) || 0,
            Number(r.effective_price) || 0,
          );
        }
        console.log('[GhostClaimModal] pledge query returned', {
          ghostProfileId,
          rowCount: count,
          dp,
          sampleRow: rows[0] ?? null,
        });
        if (count === 0) {
          console.warn('[GhostClaimModal] 0 pledges for ghostProfileId — self-dismissing without UI');
          setState({ kind: 'empty' });
        } else {
          setState({ kind: 'offer', pledgeCount: count, dp });
        }
      } catch (e) {
        console.error('[GhostClaimModal] pledge query threw', e);
        if (!cancelled) setState({ kind: 'empty' });
      }
    })();
    return () => { cancelled = true; };
  }, [ghostProfileId, enrolledProfileId]);

  // Empty → tell parent immediately so it can dismount us.
  useEffect(() => {
    if (state.kind === 'empty') onDone(false);
  }, [state, onDone]);

  // ── Claim action ─────────────────────────────────────────────────
  async function performClaim(): Promise<void> {
    if (state.kind !== 'offer') return;
    const { pledgeCount, dp } = state;
    if (!isSupabaseEnabled() || !supabase) {
      setState({ kind: 'error', pledgeCount, dp, message: 'Sign-in is offline right now.' });
      return;
    }

    setState({ kind: 'claiming', pledgeCount, dp });
    try {
      const { data, error } = await supabase.rpc(
        'claim_ghost_identity',
        { p_ghost_profile_id: ghostProfileId },
      );
      if (error) {
        console.error('[GhostClaimModal] claim_ghost_identity RPC failed', error);
        setState({ kind: 'error', pledgeCount, dp, message: "Couldn't claim. Try again in a moment." });
        return;
      }

      type Ok      = { ok: true;  claimed_profile_id: string; pledge_count?: number; settlement_count?: number; previously_claimed: boolean };
      type Refused = { ok: false; reason: string };
      const result = data as Ok | Refused;

      if (!result.ok) {
        if (result.reason === 'ALREADY_CLAIMED_BY_OTHER') {
          setState({
            kind: 'error',
            pledgeCount, dp,
            message: 'This Ghost identity has already been claimed by another account. ' +
              'If this is unexpected, contact arena@agoracle.io.',
          });
        } else {
          console.error('[GhostClaimModal] claim_ghost_identity refused', result);
          setState({ kind: 'error', pledgeCount, dp, message: "Couldn't claim. Try again in a moment." });
        }
        return;
      }

      // ── Verification gate (brief §5 #3) ─────────────────────────
      // Confirm the indirection view now resolves the Ghost to this
      // enrolled identity. If the modal said N pledges but the view
      // shows 0, log + surface "Claim recorded but display has not
      // refreshed; reload the page."
      setState({ kind: 'verifying', pledgeCount, dp });
      let verifiedCount: number | null = null;
      try {
        const { count, error: verifyErr } = await supabase
          .from('pledges')
          .select('id', { count: 'exact', head: true })
          .in(
            'user_id',
            // resolve all profile_ids whose effective owner is this enrolled identity
            await (async () => {
              const { data: rows } = await supabase!
                .from('effective_owner')
                .select('profile_id')
                .eq('effective_profile_id', enrolledProfileId);
              return ((rows ?? []) as Array<{ profile_id: string }>).map(r => r.profile_id);
            })(),
          );
        if (verifyErr) {
          console.error('[GhostClaimModal] verification query failed', verifyErr);
        } else {
          verifiedCount = count ?? 0;
        }
      } catch (e) {
        console.error('[GhostClaimModal] verification threw', e);
      }

      if (verifiedCount !== null && verifiedCount === 0 && pledgeCount > 0) {
        console.error(
          '[GhostClaimModal] claim recorded but effective_owner returned 0 pledges where %d were expected',
          pledgeCount,
        );
      }

      // Belt-and-brace: clear the Ghost id from localStorage so the
      // next cockpit boot does not re-mint or re-suggest it.
      try { window.localStorage.removeItem('agoracle_ghost_id'); } catch { /* storage denied */ }

      setState({
        kind: 'claimed',
        pledgeCount, dp,
        previously: result.previously_claimed === true,
        verifiedCount,
      });
    } catch (e) {
      console.error('[GhostClaimModal] performClaim threw', e);
      setState({ kind: 'error', pledgeCount, dp, message: "Couldn't claim. Try again in a moment." });
    }
  }

  // Suppress render while we're discovering — parent will dismount
  // us via the `empty` → onDone(false) effect if there's nothing to
  // claim.
  if (state.kind === 'loading' || state.kind === 'empty') return null;

  // ── Render ───────────────────────────────────────────────────────
  const dpText = state.dp >= 100
    ? state.dp.toFixed(0)
    : state.dp.toFixed(1);

  return (
    <div
      onClick={state.kind === 'claiming' || state.kind === 'verifying' ? undefined : () => onDone(false)}
      style={{
        position:        'fixed',
        inset:           0,
        backgroundColor: 'rgba(2,6,23,0.80)',
        backdropFilter:  'blur(3px)',
        zIndex:          1001,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#0f172a',
          border:          '1px solid #334155',
          borderTop:       '2px solid #a78bfa',
          borderRadius:    10,
          padding:         '22px 24px',
          width:           '100%',
          maxWidth:        440,
          fontFamily:      'monospace',
          boxShadow:       '0 0 40px rgba(167,139,250,0.22)',
        }}
      >
        <p style={{ color: '#c4b5fd', fontSize: 11, letterSpacing: '0.18em', margin: '0 0 14px' }}>
          CLAIM GHOST HISTORY
        </p>

        {state.kind === 'offer' && (
          <>
            <p style={{ color: '#c7d2fe', fontSize: 12, lineHeight: 1.6, margin: '0 0 8px' }}>
              You have <strong style={{ color: '#fff' }}>{state.pledgeCount}</strong>{' '}
              pledge{state.pledgeCount === 1 ? '' : 's'} and{' '}
              <strong style={{ color: '#fff' }}>{dpText}</strong> DP recorded under your
              Ghost identity.
            </p>
            <p style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.6, margin: 0 }}>
              Claim them into your new account, or skip and start fresh.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button
                onClick={() => onDone(false)}
                style={{
                  flex: 1,
                  padding:       '8px 12px',
                  fontFamily:    'monospace',
                  fontSize:      11,
                  letterSpacing: '0.18em',
                  color:         '#94a3b8',
                  background:    'transparent',
                  border:        '1px solid #334155',
                  borderRadius:  4,
                  cursor:        'pointer',
                  textTransform: 'uppercase',
                }}
              >
                Skip
              </button>
              <button
                onClick={performClaim}
                style={{
                  flex: 2,
                  padding:       '8px 12px',
                  fontFamily:    'monospace',
                  fontSize:      11,
                  letterSpacing: '0.18em',
                  color:         '#faf5ff',
                  background:    '#7c3aed',
                  border:        '1px solid #a78bfa',
                  borderRadius:  4,
                  cursor:        'pointer',
                  textTransform: 'uppercase',
                }}
              >
                Claim
              </button>
            </div>
          </>
        )}

        {(state.kind === 'claiming' || state.kind === 'verifying') && (
          <p style={{ color: '#c7d2fe', fontSize: 12, lineHeight: 1.6, margin: 0 }}>
            {state.kind === 'claiming' ? 'Recording claim…' : 'Verifying inheritance…'}
          </p>
        )}

        {state.kind === 'claimed' && (
          <>
            <p style={{ color: '#86efac', fontSize: 12, lineHeight: 1.6, margin: '0 0 8px' }}>
              {state.previously ? 'Already claimed.' : 'Claimed.'}
            </p>
            <p style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.6, margin: 0 }}>
              {state.verifiedCount === 0 && state.pledgeCount > 0
                ? 'Claim recorded but display has not refreshed yet. Reload the page if your history is not visible.'
                : 'Your Ghost history is now under your enrolled identity.'}
            </p>
            <button
              onClick={() => onDone(true)}
              style={{
                marginTop:     16,
                width:         '100%',
                padding:       '8px 12px',
                fontFamily:    'monospace',
                fontSize:      11,
                letterSpacing: '0.18em',
                color:         '#e0e7ff',
                background:    '#4338ca',
                border:        '1px solid #6366f1',
                borderRadius:  4,
                cursor:        'pointer',
                textTransform: 'uppercase',
              }}
            >
              Continue
            </button>
          </>
        )}

        {state.kind === 'error' && (
          <>
            <p style={{ color: '#fca5a5', fontSize: 12, lineHeight: 1.6, margin: 0 }}>
              {state.message}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => onDone(false)}
                style={{
                  flex: 1,
                  padding:       '8px 12px',
                  fontFamily:    'monospace',
                  fontSize:      11,
                  letterSpacing: '0.18em',
                  color:         '#94a3b8',
                  background:    'transparent',
                  border:        '1px solid #334155',
                  borderRadius:  4,
                  cursor:        'pointer',
                  textTransform: 'uppercase',
                }}
              >
                Skip
              </button>
              <button
                onClick={() => setState({ kind: 'offer', pledgeCount: state.pledgeCount, dp: state.dp })}
                style={{
                  flex: 1,
                  padding:       '8px 12px',
                  fontFamily:    'monospace',
                  fontSize:      11,
                  letterSpacing: '0.18em',
                  color:         '#faf5ff',
                  background:    '#7c3aed',
                  border:        '1px solid #a78bfa',
                  borderRadius:  4,
                  cursor:        'pointer',
                  textTransform: 'uppercase',
                }}
              >
                Retry
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
